import axios from 'axios';
import config from '../config';

const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + localStorage.getItem('access_token')
});

const getShipmentHost = () => {
    return config.config.api_shipments.merchant.shipmentHost;
};

/**
 * Generate pair IDs for pickups and dropoffs based on item matching.
 *
 * Strategy:
 * - Each pickup item unit gets a unique pair ID (e.g., "wp0_laptop_0", "wp0_laptop_1")
 * - Dropoff items are matched to pickup items by detail (item name)
 * - Quantity is respected - each unit is tracked individually
 *
 * @param {object} pickupInfo - First stop (main pickup)
 * @param {array} waypointsArray - Middle waypoints
 * @param {object} dropoffInfo - Last stop (final dropoff)
 * @param {array} pickupItems - Items at the first pickup stop
 * @param {array} dropoffItems - Items at the final dropoff stop
 * @returns {object} { firstStopPairIds, lastStopPairIds, waypointsPairIds }
 */
const generatePairIds = (pickupInfo, waypointsArray, dropoffInfo, pickupItems, dropoffItems) => {
    // Track all pickup items with their pair IDs
    // Structure: { detail: [{ pairId, waypointIndex, used: false }, ...] }
    const pickupRegistry = {};

    // Helper to normalize item detail for matching
    const normalizeDetail = (detail) => {
        return (detail || '').trim().toLowerCase();
    };

    // Helper to generate a unique pair ID
    const generatePairId = (waypointIndex, detail, unitIndex) => {
        const safeDetail = normalizeDetail(detail).replace(/[^a-z0-9]/g, '_').substring(0, 20);
        return `wp${waypointIndex}_${safeDetail}_${unitIndex}`;
    };

    // Helper to register pickup items
    const registerPickupItems = (items, waypointIndex) => {
        const pairIds = [];
        if (!items || items.length === 0) return pairIds;

        items.forEach((item) => {
            if (!item.detail || item.detail.trim() === '') return;

            const normalizedDetail = normalizeDetail(item.detail);
            const quantity = Math.max(1, parseInt(item.quantity) || 1);

            if (!pickupRegistry[normalizedDetail]) {
                pickupRegistry[normalizedDetail] = [];
            }

            // Create a pair ID for each unit of quantity
            for (let i = 0; i < quantity; i++) {
                const unitIndex = pickupRegistry[normalizedDetail].length;
                const pairId = generatePairId(waypointIndex, item.detail, unitIndex);
                pickupRegistry[normalizedDetail].push({
                    pairId,
                    waypointIndex,
                    used: false
                });
                pairIds.push(pairId);
            }
        });

        return pairIds;
    };

    // Helper to match dropoff items to pickups and get pair IDs
    const matchDropoffItems = (items) => {
        const pairIds = [];
        if (!items || items.length === 0) return pairIds;

        items.forEach((item) => {
            if (!item.detail || item.detail.trim() === '') return;

            const normalizedDetail = normalizeDetail(item.detail);
            const quantity = Math.max(1, parseInt(item.quantity) || 1);

            if (!pickupRegistry[normalizedDetail]) {
                return;
            }

            // Find available (unused) pickup pair IDs for this item
            let matched = 0;
            for (const pickupEntry of pickupRegistry[normalizedDetail]) {
                if (!pickupEntry.used && matched < quantity) {
                    pairIds.push(pickupEntry.pairId);
                    pickupEntry.used = true;
                    matched++;
                }
            }

            if (matched < quantity) {
                return;
            }
        });

        return pairIds;
    };

    // Step 1: Register all pickup items (firstStop + waypoints with pickups)
    // First stop (main pickup) - use index -1 to distinguish from waypoints
    const firstStopPickupPairIds = registerPickupItems(pickupItems, 'first');

    // Waypoints with pickups
    const waypointsPickupPairIds = waypointsArray.map((wp, index) => {
        if (wp.isWaypointPickup && wp.pickItems) {
            return registerPickupItems(wp.pickItems, index);
        }
        return [];
    });

    // Step 2: Match all dropoff items to pickups
    // Waypoints with dropoffs (process in order so earlier waypoints match first)
    const waypointsDropoffPairIds = waypointsArray.map((wp, index) => {
        if (wp.isWaypointDropoff && wp.dropItems) {
            return matchDropoffItems(wp.dropItems);
        }
        return [];
    });

    // Last stop (final dropoff)
    const lastStopDropoffPairIds = matchDropoffItems(dropoffItems);

    return {
        firstStopPairIds: {
            pickupPairIds: firstStopPickupPairIds.length > 0 ? firstStopPickupPairIds : null,
            dropoffPairIds: null
        },
        lastStopPairIds: {
            pickupPairIds: null,
            dropoffPairIds: lastStopDropoffPairIds.length > 0 ? lastStopDropoffPairIds : null
        },
        waypointsPairIds: waypointsArray.map((wp, index) => ({
            pickupPairIds: waypointsPickupPairIds[index].length > 0 ? waypointsPickupPairIds[index] : null,
            dropoffPairIds: waypointsDropoffPairIds[index].length > 0 ? waypointsDropoffPairIds[index] : null
        }))
    };
};

/**
 * Transform waypoints data to the format expected by the backend
 * @param {object} pickupInfo - First stop (fixed pickup location)
 * @param {array} waypointsArray - Middle waypoints to optimize
 * @param {object} dropoffInfo - Last stop (fixed dropoff location)
 * @param {array} pickupItems - Items at the first pickup stop
 * @param {array} dropoffItems - Items at the final dropoff stop
 * @returns {object} Formatted request payload
 */
const transformWaypointsForOptimization = (pickupInfo, waypointsArray, dropoffInfo, pickupItems, dropoffItems) => {
    // Generate pair IDs based on item matching
    const pairIds = generatePairIds(pickupInfo, waypointsArray, dropoffInfo, pickupItems, dropoffItems);

    return {
        firstStop: {
            lat: parseFloat(pickupInfo.lat),
            lng: parseFloat(pickupInfo.lng),
            isPickup: true,
            isDropoff: false,
            pickupPairIds: pairIds.firstStopPairIds.pickupPairIds,
            dropoffPairIds: pairIds.firstStopPairIds.dropoffPairIds
        },
        lastStop: {
            lat: parseFloat(dropoffInfo.lat),
            lng: parseFloat(dropoffInfo.lng),
            isPickup: false,
            isDropoff: true,
            pickupPairIds: pairIds.lastStopPairIds.pickupPairIds,
            dropoffPairIds: pairIds.lastStopPairIds.dropoffPairIds
        },
        waypoints: waypointsArray.map((wp, index) => ({
            index: index,
            lat: parseFloat(wp.waypointLat),
            lng: parseFloat(wp.waypointLng),
            isPickup: wp.isWaypointPickup || false,
            isDropoff: wp.isWaypointDropoff || false,
            pickupPairIds: pairIds.waypointsPairIds[index].pickupPairIds,
            dropoffPairIds: pairIds.waypointsPairIds[index].dropoffPairIds
        }))
    };
};

/**
 * Helper to check if items array has at least one valid item with a detail
 * @param {array} items - Items array
 * @returns {boolean} True if there's at least one valid item
 */
const hasValidItems = (items) => {
    if (!items || items.length === 0) return false;
    return items.some(item => item.detail && item.detail.trim() !== '');
};

/**
 * Validate that all required data is present for route optimization
 * @param {object} pickupInfo - First stop
 * @param {array} waypointsArray - Middle waypoints
 * @param {object} dropoffInfo - Last stop
 * @param {array} pickupItems - Items at the first pickup stop
 * @param {array} dropoffItems - Items at the final dropoff stop
 * @returns {object} { isValid: boolean, error: string|null }
 */
export const validateRouteData = (pickupInfo, waypointsArray, dropoffInfo, pickupItems, dropoffItems) => {
    // Check pickup info
    if (!pickupInfo || !pickupInfo.lat || !pickupInfo.lng) {
        return { isValid: false, error: 'Pickup location is missing coordinates' };
    }

    // Check dropoff info
    if (!dropoffInfo || !dropoffInfo.lat || !dropoffInfo.lng) {
        return { isValid: false, error: 'Dropoff location is missing coordinates' };
    }

    // Check waypoints
    if (!waypointsArray || waypointsArray.length < 2) {
        return { isValid: false, error: 'At least 2 waypoints are required for optimization' };
    }

    // Check each waypoint has coordinates and items based on pickup/dropoff selection
    for (let i = 0; i < waypointsArray.length; i++) {
        const wp = waypointsArray[i];
        if (!wp.waypointLat || !wp.waypointLng) {
            return { isValid: false, error: `Waypoint ${i + 1} is missing coordinates` };
        }

        // At least one checkbox (pickup or dropoff) must be selected
        if (!wp.isWaypointPickup && !wp.isWaypointDropoff) {
            return { isValid: false, error: `Waypoint ${i + 1} must have pickup or dropoff selected` };
        }

        // If pickup checkbox is checked, verify it has pickup items
        if (wp.isWaypointPickup && !hasValidItems(wp.pickItems)) {
            return { isValid: false, error: `Waypoint ${i + 1} has pickup selected but no pickup items` };
        }

        // If dropoff checkbox is checked, verify it has dropoff items
        if (wp.isWaypointDropoff && !hasValidItems(wp.dropItems)) {
            return { isValid: false, error: `Waypoint ${i + 1} has dropoff selected but no dropoff items` };
        }
    }

    // First stop must have pickup items
    if (!hasValidItems(pickupItems)) {
        return { isValid: false, error: 'First pickup location must have pickup items' };
    }

    // Last stop must have dropoff items
    if (!hasValidItems(dropoffItems)) {
        return { isValid: false, error: 'Last dropoff location must have dropoff items' };
    }

    // Validate that pickup and dropoff items match (each item picked up must be dropped off)
    // Count total pickup items by detail
    const pickupCounts = {};

    // Count first stop pickup items
    if (pickupItems) {
        pickupItems.forEach(item => {
            if (item.detail && item.detail.trim() !== '') {
                const key = item.detail.trim().toLowerCase();
                const qty = parseInt(item.quantity) || 1;
                pickupCounts[key] = (pickupCounts[key] || 0) + qty;
            }
        });
    }

    // Count waypoint pickup items
    waypointsArray.forEach(wp => {
        if (wp.isWaypointPickup && wp.pickItems) {
            wp.pickItems.forEach(item => {
                if (item.detail && item.detail.trim() !== '') {
                    const key = item.detail.trim().toLowerCase();
                    const qty = parseInt(item.quantity) || 1;
                    pickupCounts[key] = (pickupCounts[key] || 0) + qty;
                }
            });
        }
    });

    // Count total dropoff items by detail
    const dropoffCounts = {};

    // Count last stop dropoff items
    if (dropoffItems) {
        dropoffItems.forEach(item => {
            if (item.detail && item.detail.trim() !== '') {
                const key = item.detail.trim().toLowerCase();
                const qty = parseInt(item.quantity) || 1;
                dropoffCounts[key] = (dropoffCounts[key] || 0) + qty;
            }
        });
    }

    // Count waypoint dropoff items
    waypointsArray.forEach(wp => {
        if (wp.isWaypointDropoff && wp.dropItems) {
            wp.dropItems.forEach(item => {
                if (item.detail && item.detail.trim() !== '') {
                    const key = item.detail.trim().toLowerCase();
                    const qty = parseInt(item.quantity) || 1;
                    dropoffCounts[key] = (dropoffCounts[key] || 0) + qty;
                }
            });
        }
    });

    // Check that each pickup item has a matching dropoff
    for (const [itemName, pickupQty] of Object.entries(pickupCounts)) {
        const dropoffQty = dropoffCounts[itemName] || 0;
        if (pickupQty !== dropoffQty) {
            return {
                isValid: false,
                error: `Item "${itemName}" has ${pickupQty} pickup(s) but ${dropoffQty} dropoff(s). They must match.`
            };
        }
    }

    // Check for dropoff items without matching pickups
    for (const [itemName, dropoffQty] of Object.entries(dropoffCounts)) {
        if (!pickupCounts[itemName]) {
            return {
                isValid: false,
                error: `Item "${itemName}" has ${dropoffQty} dropoff(s) but no matching pickup.`
            };
        }
    }

    return { isValid: true, error: null };
};

/**
 * Reorder the waypoints array based on optimized order indices
 * @param {array} waypointsArray - Original waypoints array
 * @param {array} optimizedOrder - Array of 0-based indices in optimized order
 * @returns {array} Reordered waypoints array with new object references
 */
export const reorderWaypoints = (waypointsArray, optimizedOrder) => {
    // Validate that backend returned correct number of indices
    if (optimizedOrder.length !== waypointsArray.length) {
        return waypointsArray;
    }

    // Reorder using 0-based indices from backend
    // Create new object references so React detects the change
    return optimizedOrder.map(i => ({ ...waypointsArray[i] }));
};

/**
 * Call the backend to optimize the route
 * @param {object} pickupInfo - First stop (fixed pickup location)
 * @param {array} waypointsArray - Middle waypoints to optimize
 * @param {object} dropoffInfo - Last stop (fixed dropoff location)
 * @param {array} pickupItems - Items at the first pickup stop (optional, for pair ID generation)
 * @param {array} dropoffItems - Items at the final dropoff stop (optional, for pair ID generation)
 * @returns {Promise} Optimization result with optimizedOrder array
 */
export const optimizeRoute = async (pickupInfo, waypointsArray, dropoffInfo, pickupItems, dropoffItems) => {
    // Validate input data
    const validation = validateRouteData(pickupInfo, waypointsArray, dropoffInfo, pickupItems, dropoffItems);
    if (!validation.isValid) {
        throw new Error(validation.error);
    }

    // Transform data for API
    const payload = transformWaypointsForOptimization(pickupInfo, waypointsArray, dropoffInfo, pickupItems, dropoffItems);

    try {
        const response = await axios.post(`${getShipmentHost()}/shipments/routes/optimize`, payload, {
            headers: getAuthHeaders(),
            timeout: 30000 // 30 second timeout for optimization
        });

        // Check for successful response using backend's format
        if (!response.data || response.data.status !== 'successful' || !response.data.hasPayload) {
            throw new Error(response.data.message || 'Route optimization failed');
        }

        return {
            success: true,
            optimizedOrder: response.data.payload.optimizedOrder,
            estimatedDistance: response.data.payload.estimatedDistance,
            estimatedDuration: response.data.payload.estimatedDuration
        };
    } catch (error) {
        // Handle specific error types
        if (error.response) {
            // Server responded with error
            const status = error.response.status;
            const message = (error.response.data && error.response.data.message) || (error.response.data && error.response.data.error);

            if (status === 429) {
                throw new Error('Rate limit exceeded. Please try again in a few moments.');
            } else if (status === 400) {
                throw new Error(message || 'Invalid route data provided.');
            } else if (status === 502 || status === 503) {
                throw new Error('Route optimization service is temporarily unavailable.');
            }

            throw new Error(message || 'Failed to optimize route.');
        } else if (error.code === 'ECONNABORTED') {
            throw new Error('Request timed out. Please try again.');
        }

        throw error;
    }
};
